import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { getUserApps, deleteApp, UserApp } from '../services/appRegistry';
import { getIntegrationStatus } from '../services/integrationsApi';
import {
  SpaceBackground,
  Header,
  ChatBar,
  BottomNav,
  Sidebar,
  SettingsPanel,
  PageTitle,
  Plus,
  Grid,
  Calendar,
  Mail,
  MessageCircle,
  Globe,
  Smartphone,
  Layout,
  MoreVertical,
  FolderPlus,
  Trash2,
  RefreshCw
} from '../components/shared';

// Icon mapping for app types
const iconMap: Record<string, React.ElementType> = {
  mail: Mail,
  calendar: Calendar,
  message: MessageCircle,
  globe: Globe,
  layout: Layout,
  smartphone: Smartphone,
  grid: Grid,
  default: Grid
};

// Color mapping for app types
const colorMap: Record<string, string> = {
  mail: 'from-blue-400 to-cyan-400',
  calendar: 'from-purple-400 to-pink-400',
  message: 'from-green-400 to-emerald-400',
  globe: 'from-orange-400 to-yellow-400',
  default: 'from-cyan-400 to-blue-400'
};

// Map API app to UI format
const mapApiAppToUiApp = (apiApp: UserApp, index: number): App => ({
  id: apiApp.app_id || `${Date.now()}-${Math.random()}-${index}`,
  app_id: apiApp.app_id,
  name: apiApp.name,
  icon: iconMap[apiApp.icon] || iconMap.default,
  color: colorMap[apiApp.icon] || colorMap.default,
  description: apiApp.description,
  metadata: apiApp.metadata
});

// Brand logos for integrations — will be used when connected apps show provider icons
// Logos available in git history (commit c5f6e7b) when needed for Trust-Drip OAuth tiles

// Trust-Drip handles app connections through AI conversation.
// When user asks about calendar/email, AI suggests connecting and initiates OAuth.
// Connected apps then appear in the MY APPS grid above.

interface App {
  id: number | string;
  app_id?: string;
  name: string;
  icon: React.ElementType;
  color: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

// Full-screen modal menu for app actions
const ActionModal = ({
  isOpen,
  onClose,
  app,
  onAddToProject,
  onUpdateApp,
  onDelete,
  isUserApp
}: {
  isOpen: boolean;
  onClose: () => void;
  app: App | null;
  onAddToProject: () => void;
  onUpdateApp: () => void;
  onDelete: () => void;
  isUserApp: boolean;
}) => {
  if (!isOpen || !app) return null;
  const Icon = app.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-sm mx-4 bg-black/95 backdrop-blur-2xl border border-cyan-400/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)]">
        {/* Header */}
        <div className="p-4 border-b border-cyan-400/20">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-cyan-300 font-semibold text-lg">{app.name}</h3>
              <p className="text-cyan-400/50 text-sm mt-0.5">{isUserApp ? 'My App' : 'Connected App'}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-2">
          <button
            onClick={() => { onUpdateApp(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-4 rounded-xl transition-all"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Update app</span>
          </button>
          <button
            onClick={() => { onAddToProject(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-4 rounded-xl transition-all"
          >
            <FolderPlus className="w-5 h-5" />
            <span>Add to project</span>
          </button>
          {isUserApp && (
            <button
              onClick={() => { onDelete(); onClose(); }}
              className="w-full text-left px-4 py-4 text-base text-red-400/90 hover:bg-red-400/10 hover:text-red-300 flex items-center space-x-4 rounded-xl transition-all border-t border-cyan-400/10 mt-2"
            >
              <Trash2 className="w-5 h-5" />
              <span>Delete app</span>
            </button>
          )}
        </div>

        {/* Cancel button */}
        <div className="p-4 border-t border-cyan-400/20">
          <button
            onClick={onClose}
            className="w-full py-3 text-cyan-400 font-semibold bg-cyan-400/10 hover:bg-cyan-400/20 rounded-xl transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AppCard = ({ app, onClick, onMenuOpen }: { app: App; onClick: () => void; onMenuOpen: () => void }) => {
  const Icon = app.icon;

  return (
    <div className="relative group">
      <button
        onClick={onClick}
        className="w-full flex flex-col items-center space-y-3 p-4 rounded-2xl bg-cyan-500/[0.03] hover:bg-cyan-400/[0.1] backdrop-blur-xl border border-cyan-400/20 hover:border-cyan-400/50 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(34,211,238,0.3)]"
      >
        <div className={`relative w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${app.color} flex items-center justify-center shadow-lg group-hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]`}>
          <Icon className="w-6 h-6 md:w-8 md:h-8 text-white drop-shadow-lg" />

          {app.metadata?.status === 'draft' && (
            <div className="absolute -top-2 -right-2 bg-yellow-500/90 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow-md transform rotate-12 border border-yellow-300/50">
              DRAFT
            </div>
          )}
        </div>
        <span className="text-cyan-400/80 text-xs font-semibold text-center group-hover:text-cyan-300 transition-colors line-clamp-2">
          {app.name}
        </span>
      </button>

      {/* Menu button - appears on hover */}
      <button
        onClick={(e) => { e.stopPropagation(); onMenuOpen(); }}
        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-cyan-400/30 opacity-0 group-hover:opacity-100 hover:bg-cyan-400/20 transition-all"
      >
        <MoreVertical className="w-4 h-4 text-cyan-400" />
      </button>
    </div>
  );
};

const CreateAppButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="group flex flex-col items-center space-y-3 p-4 rounded-2xl bg-cyan-500/[0.03] hover:bg-cyan-400/[0.1] backdrop-blur-xl border border-dashed border-cyan-400/30 hover:border-cyan-400/60 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(34,211,238,0.3)]"
  >
    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center group-hover:bg-cyan-400/20 group-hover:border-cyan-400/50 transition-all">
      <Plus className="w-6 h-6 md:w-8 md:h-8 text-cyan-400/60 group-hover:text-cyan-300" />
    </div>
    <span className="text-cyan-400/60 text-xs font-semibold text-center group-hover:text-cyan-300 transition-colors">
      Create App
    </span>
  </button>
);

export default function MyAppsScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [menuApp, setMenuApp] = useState<{ app: App; isUserApp: boolean } | null>(null);
  const [userApps, setUserApps] = useState<App[]>([]);
  const [_isLoading, setIsLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);
  const [connectedProviders, setConnectedProviders] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch apps from API
  const fetchApps = useCallback(async () => {
    if (!user?.uid) return;

    setIsLoading(true);
    setError(null);

    try {
      const apps = await getUserApps(user.uid);
      const uiApps = apps.map(mapApiAppToUiApp);
      setUserApps(uiApps);
    } catch (err) {
      console.error('Error fetching apps:', err);
      setError('Failed to load apps');
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]);

  // Fetch apps + integration status on mount
  useEffect(() => {
    fetchApps();
    // Check which integrations are connected
    getIntegrationStatus().then(status => {
      const connected = new Set<string>();
      if (status.calendar?.connected) connected.add('google_calendar');
      if (status.email?.connected) connected.add('gmail');
      setConnectedProviders(connected);
    }).catch(() => { });
  }, [fetchApps]);

  // Refresh app list when page becomes visible (e.g., returning from app builder)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchApps();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchApps]);

  const handleCreateApp = () => {
    navigate('/apps/create');
  };

  const handleAppClick = (app: App) => {
    const appId = app.app_id || app.id;
    // If it's a draft, go back to the builder
    if (app.metadata?.status === 'draft') {
      // Strip non-serializable properties (like React components) before navigating
      const { icon, color, ...serializableApp } = app;
      navigate('/apps/create', { state: { editApp: serializableApp } });
    } else {
      // Otherwise navigate to app runner to use the live app
      navigate(`/apps/${appId}`);
    }
  };

  const handleAddToProject = (_appId: number | string) => {
    // Navigate to projects where user can link apps
    navigate('/projects');
  };

  const handleUpdateApp = (appId: number | string) => {
    // Find the app and navigate to builder
    const app = userApps.find(a => a.id === appId);
    if (app) {
      // Strip non-serializable properties (like React components) before navigating
      const { icon, color, ...serializableApp } = app;
      navigate('/apps/create', { state: { editApp: serializableApp } });
    }
  };

  const handleDeleteApp = async (appId: number | string) => {
    // Confirmation dialog — never delete without asking
    if (!window.confirm('Are you sure you want to delete this app?')) return;

    const app = userApps.find(a => a.id === appId);
    if (!app) return;

    if (!app.app_id) {
      // Local-only app (no backend ID) — just remove from UI
      setUserApps(prev => prev.filter(a => a.id !== appId));
      return;
    }

    try {
      await deleteApp(app.app_id);
      // Only remove the specific app on success
      setUserApps(prev => prev.filter(a => a.id !== appId));
    } catch (err) {
      console.error('Error deleting app:', err);
      setError('Failed to delete app');
      // Don't clear state on failure
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Content Area with scroll fade */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex flex-col justify-start pt-24 md:pt-28 px-4 overflow-y-auto w-full items-center"
          style={{
            paddingBottom: 240,
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))'
          }}
        >
          <div className="w-full max-w-3xl">
            <PageTitle title="My Apps" />

            {/* My Apps Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest flex items-center space-x-2">
                  <Smartphone className="w-4 h-4" />
                  <span>My Apps ({userApps.length})</span>
                </h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                <CreateAppButton onClick={handleCreateApp} />
                {userApps.map(app => (
                  <AppCard
                    key={app.id}
                    app={app}
                    onClick={() => handleAppClick(app)}
                    onMenuOpen={() => setMenuApp({ app, isUserApp: true })}
                  />
                ))}
              </div>
            </div>

            {/* Connected Apps Section — shows only apps the user has actually connected via Trust-Drip */}
            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest flex items-center space-x-2">
                  <Grid className="w-4 h-4" />
                  <span>Connected Apps</span>
                </h3>
              </div>

              {connectedProviders.size > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {Array.from(connectedProviders).map(provider => {
                    const providerInfo: Record<string, { name: string; icon: React.ElementType; color: string }> = {
                      google_calendar: { name: 'Google Calendar', icon: Calendar, color: 'from-purple-400 to-pink-400' },
                      gmail: { name: 'Gmail', icon: Mail, color: 'from-blue-400 to-cyan-400' },
                    };
                    const info = providerInfo[provider];
                    if (!info) return null;
                    return (
                      <AppCard
                        key={provider}
                        app={{ id: Date.now(), name: info.name, icon: info.icon, color: info.color }}
                        onClick={() => {
                          const typeMap: Record<string, string> = { google_calendar: 'calendar', gmail: 'email' };
                          navigate(`/integrations/${typeMap[provider] || provider}`);
                        }}
                        onMenuOpen={() => setMenuApp({ app: { id: Date.now(), name: info.name, icon: info.icon, color: info.color }, isUserApp: false })}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="p-6 rounded-2xl border border-cyan-400/10 bg-cyan-500/[0.02] text-center">
                  <p className="text-cyan-400/40 text-sm">
                    No apps connected yet. Ask SuperJarvis to connect your calendar, email, or other services.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={() => {
              if (inputText.trim()) {
                const executionMode = chatMode === 'Fast' ? 'fast' : 'super';
                navigate(`/?message=${encodeURIComponent(inputText.trim())}&mode=${executionMode}`);
                setInputText('');
              }
            }}
          />
          <BottomNav activeTab="apps" />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }} userName={user?.displayName || user?.email?.split("@")[0]} userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />

      {/* Action Modal */}
      <ActionModal
        isOpen={!!menuApp}
        onClose={() => setMenuApp(null)}
        app={menuApp?.app || null}
        isUserApp={menuApp?.isUserApp || false}
        onAddToProject={() => menuApp && handleAddToProject(menuApp.app.id)}
        onUpdateApp={() => menuApp && handleUpdateApp(menuApp.app.id)}
        onDelete={() => menuApp && handleDeleteApp(menuApp.app.id)}
      />
    </div>
  );
}
